import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import FlodeskForm from './FlodeskForm';
import { Calendar, Users, Star, Zap, Mic, Camera, Building, Heart, Shield, Palette, Rocket, TrendingUp, Check, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Component to render Eventbrite-style ticket buttons
// This handles the Eventbrite widget by creating buttons that trigger the modal
const EventbriteTicketButton = ({ embedCode, buttonLabel, buttonStyle, ticketIndex }) => {
  const buttonRef = useRef(null);
  const buttonId = `eb-button-${ticketIndex}-${Math.random().toString(36).substr(2, 9)}`;
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (!embedCode) return;
    
    // Parse the embed code to extract the event ID
    const eventIdMatch = embedCode.match(/eventId\s*[:=]\s*['"]?(\d+)['"]?/);
    const eventId = eventIdMatch ? eventIdMatch[1] : null;
    
    if (!eventId) {
      console.warn('Could not extract Eventbrite event ID from embed code');
      setIsReady(true);
      return;
    }
    
    // Load Eventbrite widgets script if not already loaded
    const loadEventbriteScript = () => {
      return new Promise((resolve) => {
        if (window.EBWidgets) {
          resolve();
          return;
        }
        
        const existingScript = document.querySelector('script[src*="eventbrite.com/js/checkout"]');
        if (existingScript) {
          // Script is loading, wait for it
          const checkReady = setInterval(() => {
            if (window.EBWidgets) {
              clearInterval(checkReady);
              resolve();
            }
          }, 100);
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkReady);
            resolve();
          }, 10000);
          return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://www.eventbrite.com/static/widgets/eb_widgets.js';
        script.async = true;
        script.onload = () => {
          // Give it a moment to initialize
          setTimeout(resolve, 100);
        };
        script.onerror = () => {
          console.error('Failed to load Eventbrite widgets script');
          resolve();
        };
        document.head.appendChild(script);
      });
    };
    
    // Initialize the widget for this button
    const initWidget = async () => {
      await loadEventbriteScript();
      
      if (window.EBWidgets && buttonRef.current) {
        try {
          window.EBWidgets.createWidget({
            widgetType: 'checkout',
            eventId: eventId,
            modal: true,
            modalTriggerElementId: buttonId,
            onOrderComplete: () => {
              console.log('Eventbrite order complete');
            }
          });
          setIsReady(true);
        } catch (err) {
          console.error('Error initializing Eventbrite widget:', err);
          setIsReady(true);
        }
      } else {
        setIsReady(true);
      }
    };
    
    initWidget();
  }, [embedCode, buttonId]);
  
  // If no embed code, don't render
  if (!embedCode) return null;
  
  return (
    <button
      ref={buttonRef}
      id={buttonId}
      type="button"
      className={buttonStyle}
      data-testid={`eventbrite-button-${ticketIndex}`}
    >
      {buttonLabel}
    </button>
  );
};

// Generic embed code component for non-Eventbrite embeds
const EmbedCode = ({ html, className = '' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!html || !containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = '';

    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extract scripts - separate external and inline
    const scripts = Array.from(tempDiv.querySelectorAll('script'));
    const externalScripts = scripts.filter(s => s.src);
    const inlineScripts = scripts.filter(s => !s.src && s.innerHTML);
    
    // Remove scripts from HTML content and insert non-script content first
    scripts.forEach(s => s.remove());
    container.innerHTML = tempDiv.innerHTML;

    // Load external script with proper onload handling
    const loadScript = (src) => {
      return new Promise((resolve) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => {
          setTimeout(resolve, 100); // Small delay to ensure initialization
        };
        script.onerror = () => {
          console.error('Failed to load script:', src);
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    // Execute scripts in order
    (async () => {
      // Load all external scripts first
      for (const script of externalScripts) {
        await loadScript(script.src);
      }
      
      // Then execute inline scripts
      for (const script of inlineScripts) {
        try {
          // eslint-disable-next-line no-eval
          (0, eval)(script.innerHTML);
        } catch (err) {
          console.error('Error executing inline script:', err);
        }
      }
    })();

    return () => {
      container.innerHTML = '';
    };
  }, [html]);

  return <div ref={containerRef} className={className} />;
};

// Helper function to handle CTA link clicks - opens in same window, smooth scroll for anchors
const handleLinkClick = (e, href, navigate) => {
  if (!href) return;
  
  // Handle anchor links (e.g., #tickets, #about)
  if (href.startsWith('#')) {
    e.preventDefault();
    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return;
  }
  
  // Handle internal links (same domain or relative paths)
  if (href.startsWith('/') || href.startsWith(window.location.origin)) {
    e.preventDefault();
    const path = href.startsWith('/') ? href : new URL(href).pathname;
    navigate(path);
    return;
  }
  
  // External links - open in same window (no target="_blank")
  // The default <a> behavior will handle this
};

const ICONS = {
  calendar: Calendar,
  users: Users,
  star: Star,
  zap: Zap,
  mic: Mic,
  camera: Camera,
  building: Building,
  heart: Heart,
  shield: Shield,
  palette: Palette,
  rocket: Rocket,
  'trending-up': TrendingUp,
};

// Hero Section Component
const HeroSection = ({ data, pageType, accentColor, navigate }) => {
  const hasBackgroundImage = data.background_image;
  
  const bgClass = hasBackgroundImage
    ? 'text-white'
    : pageType === 'expo' 
    ? 'bg-gray-900 text-white' 
    : pageType === 'festival' 
    ? 'bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white'
    : 'bg-gray-50 text-gray-900';

  const sectionStyle = hasBackgroundImage
    ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${data.background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {};

  return (
    <section 
      className={`py-20 md:py-32 ${bgClass}`} 
      style={sectionStyle}
      data-testid="hero-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {data.badge_text && (
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-6 ${
            hasBackgroundImage || pageType !== 'regular' ? 'bg-white/20 backdrop-blur' : 'bg-gray-200 text-gray-800'
          }`}>
            {data.badge_text}
          </span>
        )}
        <h1 className="text-4xl md:text-6xl font-bold mb-4">{data.headline}</h1>
        {/* Event Date - prominently displayed below headline */}
        {data.event_date && (
          <p 
            className="text-2xl md:text-3xl font-semibold mb-6"
            style={{ color: accentColor || '#19c85d' }}
          >
            {data.event_date}
          </p>
        )}
        {data.subheadline && (
          <p className={`text-xl md:text-2xl mb-4 ${hasBackgroundImage || pageType !== 'regular' ? 'text-white/80' : 'text-gray-600'}`}>
            {data.subheadline}
          </p>
        )}
        {data.description && (
          <p className={`text-lg max-w-3xl mx-auto mb-8 ${hasBackgroundImage || pageType !== 'regular' ? 'text-white/70' : 'text-gray-500'}`}>
            {data.description}
          </p>
        )}
        {(data.primary_cta_label || data.secondary_cta_label) && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {data.primary_cta_label && (
              <a
                href={data.primary_cta_link || '#'}
                onClick={(e) => handleLinkClick(e, data.primary_cta_link, navigate)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all ${
                  hasBackgroundImage || pageType !== 'regular'
                    ? 'bg-white text-gray-900 hover:bg-gray-100'
                    : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {data.primary_cta_label}
              </a>
            )}
            {data.secondary_cta_label && (
              <a
                href={data.secondary_cta_link || '#'}
                onClick={(e) => handleLinkClick(e, data.secondary_cta_link, navigate)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all border-2 ${
                  hasBackgroundImage || pageType !== 'regular'
                    ? 'border-white text-white hover:bg-white/10'
                    : 'border-black text-black hover:bg-gray-100'
                }`}
              >
                {data.secondary_cta_label}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// Feature Grid Section Component
const FeatureGridSection = ({ data, pageType, navigate }) => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="feature-grid-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {data.section_title && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {data.section_title}
            </h2>
            {data.section_description && (
              <div 
                className="text-lg text-gray-600 max-w-3xl mx-auto [&>p]:mb-2 [&>b]:font-semibold [&>strong]:font-semibold [&>i]:italic [&>em]:italic [&>a]:text-gray-600 [&>a]:underline [&>a:hover]:text-gray-400 [&>h1]:text-4xl [&>h1]:font-extrabold [&>h1]:mb-3 [&>h1]:text-gray-900 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:text-gray-900 [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:text-gray-800 [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mb-2 [&>h4]:text-gray-800 [&>h5]:text-lg [&>h5]:font-medium [&>h5]:mb-2 [&>h5]:text-gray-700 [&>h6]:text-base [&>h6]:font-medium [&>h6]:mb-1 [&>h6]:text-gray-700 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-2 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-2 [&>li]:mb-1"
                dangerouslySetInnerHTML={{ __html: data.section_description }}
              />
            )}
          </div>
        )}
        
        {/* Features Grid */}
        {data.features && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.features.map((feature, index) => (
              <div key={index} className="text-center p-6">
                {feature.icon && (
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    {feature.icon === 'mic' && (
                      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    )}
                    {feature.icon === 'users' && (
                      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    )}
                    {feature.icon === 'camera' && (
                      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                    {feature.icon === 'calendar' && (
                      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                    {!['mic', 'users', 'camera', 'calendar'].includes(feature.icon) && (
                      <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                )}
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* Speakers */}
        {data.speakers && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.speakers.map((speaker, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center">
                {speaker.image && (
                  <img
                    src={speaker.image}
                    alt={speaker.name}
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  />
                )}
                <h3 className="font-bold text-gray-900 mb-1">{speaker.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{speaker.title}</p>
                {speaker.topic && (
                  <p className="text-sm font-medium text-gray-800">&ldquo;{speaker.topic}&rdquo;</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Schedule - Responsive layout */}
        {data.schedule && (
          <div className="max-w-4xl mx-auto space-y-4 px-4">
            {data.schedule.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 md:p-5">
                {/* Mobile: Stack vertically, Desktop: Horizontal */}
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
                  {/* Time and Image row on mobile, inline on desktop */}
                  <div className="flex items-center gap-3 md:gap-4">
                    {/* Time */}
                    <div className="font-mono text-sm font-medium text-gray-600 whitespace-nowrap">
                      {item.time}
                    </div>
                    {/* Speaker Image */}
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.speaker || item.title}
                        className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    {item.speaker && (
                      <p className="text-sm text-gray-600">{item.speaker}</p>
                    )}
                    {item.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tickets */}
        {data.tickets && (
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {data.tickets.map((ticket, index) => (
                <div key={index} className="flex flex-col" data-testid={`ticket-card-${index}`}>
                  <div className={`rounded-xl p-8 flex-1 ${ticket.highlighted ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
                    {ticket.badge && (
                      <span className="inline-block px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full mb-4">
                        {ticket.badge}
                      </span>
                    )}
                    <h3 className={`text-2xl font-bold mb-2 ${ticket.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.name}
                    </h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold">{ticket.price}</span>
                      {ticket.original_price && ticket.original_price !== ticket.price && (
                        <span className="ml-2 text-lg line-through text-gray-400">{ticket.original_price}</span>
                      )}
                    </div>
                    <ul className="space-y-2 mb-6">
                      {ticket.features?.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <Check size={16} className={ticket.highlighted ? 'text-green-400' : 'text-green-600'} />
                          <span className={ticket.highlighted ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Buttons section - 2 buttons per ticket */}
                    <div className="space-y-3">
                      {/* If cta_link is provided, show regular link buttons */}
                      {ticket.cta_link ? (
                        <>
                          <a
                            href={ticket.cta_link}
                            onClick={(e) => handleLinkClick(e, ticket.cta_link, navigate)}
                            className={`block text-center py-3 px-6 rounded-lg font-semibold transition-all ${
                              ticket.highlighted
                                ? 'bg-white text-gray-900 hover:bg-gray-100'
                                : 'bg-black text-white hover:bg-gray-800'
                            }`}
                            data-testid={`ticket-${index}-primary-btn`}
                          >
                            {ticket.cta_label || 'Buy Now'}
                          </a>
                          <a
                            href={ticket.cta_link}
                            onClick={(e) => handleLinkClick(e, ticket.cta_link, navigate)}
                            className={`block text-center py-3 px-6 rounded-lg font-semibold transition-all border-2 ${
                              ticket.highlighted
                                ? 'border-white text-white hover:bg-white/10'
                                : 'border-black text-black hover:bg-gray-100'
                            }`}
                            data-testid={`ticket-${index}-secondary-btn`}
                          >
                            Learn More
                          </a>
                        </>
                      ) : data.ticket_embed_code ? (
                        /* If no cta_link but embed code exists, show Eventbrite button */
                        <EventbriteTicketButton
                          embedCode={data.ticket_embed_code}
                          buttonLabel={ticket.cta_label || 'Get Tickets'}
                          buttonStyle={`w-full text-center py-3 px-6 rounded-lg font-semibold transition-all cursor-pointer ${
                            ticket.highlighted
                              ? 'bg-white text-gray-900 hover:bg-gray-100'
                              : 'bg-black text-white hover:bg-gray-800'
                          }`}
                          ticketIndex={`${index}-primary`}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials with Author Images */}
        {data.testimonials && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-6">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="flex items-center gap-4">
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 font-semibold text-lg">
                        {testimonial.author?.charAt(0) || '?'}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    {testimonial.title && (
                      <p className="text-sm text-gray-600">{testimonial.title}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAQs */}
        {data.faqs && (
          <div className="max-w-3xl mx-auto space-y-4">
            {data.faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        )}

        {/* Steps */}
        {data.steps && (
          <div className="grid md:grid-cols-4 gap-6">
            {data.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                <p 
                  className="text-sm text-gray-600"
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Benefits */}
        {data.benefits && (
          <div className="bg-gray-50 rounded-xl p-8">
            <ul className="space-y-3">
              {data.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <Check size={20} className="text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        {data.cta_label && data.cta_link && (
          <div className="text-center mt-12">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-8 py-4 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-gray-600">
          {answer}
        </div>
      )}
    </div>
  );
};

// CTA Strip / Stats Section Component
const CTAStripSection = ({ data, pageType, navigate }) => {
  const bgClass = pageType === 'expo'
    ? 'bg-gray-900 text-white'
    : pageType === 'festival'
    ? 'bg-purple-900 text-white'
    : 'bg-gray-100 text-gray-900';

  return (
    <section className={`py-12 ${bgClass}`} data-testid="cta-strip-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {data.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {data.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className={pageType === 'regular' ? 'text-gray-600' : 'text-white/70'}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        )}
        {data.cta_label && data.cta_link && (
          <div className="text-center mt-8">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className={`inline-block px-6 py-3 rounded-lg font-semibold transition-all ${
                pageType === 'regular'
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Image + Text Section Component
const ImageTextSection = ({ data, pageType, navigate }) => {
  const isReversed = data.image_position === 'right';

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="image-text-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid md:grid-cols-2 gap-12 items-center ${isReversed ? 'md:flex-row-reverse' : ''}`}>
          <div className={isReversed ? 'md:order-2' : ''}>
            {data.image && (
              <img
                src={data.image}
                alt={data.heading}
                className="rounded-xl shadow-lg w-full"
              />
            )}
          </div>
          <div className={isReversed ? 'md:order-1' : ''}>
            {data.subheading && (
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                {data.subheading}
              </p>
            )}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {data.heading}
            </h2>
            <div 
              className="rich-content prose prose-lg text-gray-600 max-w-none"
              dangerouslySetInnerHTML={{ __html: data.content || '' }}
            />
            {data.cta_label && data.cta_link && (
              <div className="mt-6">
                <a
                  href={data.cta_link}
                  onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
                  className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
                >
                  {data.cta_label}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Video + Text Section Component
const VideoTextSection = ({ data, pageType, navigate }) => {
  const isReversed = data.video_position === 'right';

  const renderVideo = () => {
    // If embed code is provided, use it
    if (data.video_source === 'embed' || (!data.video_source && data.video_embed)) {
      if (data.video_embed) {
        return (
          <div 
            className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
            dangerouslySetInnerHTML={{ __html: data.video_embed }}
          />
        );
      }
    }
    
    // If uploaded video URL is provided
    if (data.video_url) {
      return (
        <video 
          src={data.video_url} 
          controls 
          className="w-full h-full object-cover"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    // Placeholder
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">Video will appear here</p>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="video-text-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid md:grid-cols-2 gap-12 items-center ${isReversed ? 'md:flex-row-reverse' : ''}`}>
          <div className={`aspect-video rounded-xl overflow-hidden bg-gray-100 ${isReversed ? 'md:order-2' : ''}`}>
            {renderVideo()}
          </div>
          <div className={isReversed ? 'md:order-1' : ''}>
            {data.subheading && (
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                {data.subheading}
              </p>
            )}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {data.heading}
            </h2>
            <div 
              className="rich-content prose prose-lg text-gray-600 max-w-none"
              dangerouslySetInnerHTML={{ __html: data.content || '' }}
            />
            {data.cta_label && data.cta_link && (
              <div className="mt-6">
                <a
                  href={data.cta_link}
                  onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
                  className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
                >
                  {data.cta_label}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Text Section Component
const TextSection = ({ data, navigate }) => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="text-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="rich-content prose prose-lg max-w-none text-gray-700"
          dangerouslySetInnerHTML={{ __html: data.content || '' }}
        />
        {data.cta_label && data.cta_link && (
          <div className="text-center mt-8">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Embed Section Component - Supports iframes, scripts, and custom embeds
const EmbedSection = ({ data, navigate }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !data.embed_code) return;

    // Clear previous content
    container.innerHTML = '';

    // Create a temporary container to parse the embed code
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = data.embed_code;

    // Process and execute scripts
    const scripts = tempContainer.querySelectorAll('script');
    const nonScriptNodes = [];
    
    // Collect non-script nodes
    tempContainer.childNodes.forEach(node => {
      if (node.nodeName !== 'SCRIPT') {
        nonScriptNodes.push(node.cloneNode(true));
      }
    });

    // Append non-script elements first
    nonScriptNodes.forEach(node => {
      container.appendChild(node);
    });

    // Then execute scripts by recreating them (innerHTML doesn't execute scripts)
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      
      // Copy all attributes
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Copy inline content
      if (oldScript.innerHTML) {
        newScript.innerHTML = oldScript.innerHTML;
      }
      
      // Append to container to execute
      container.appendChild(newScript);
    });

    // Cleanup function
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [data.embed_code]);

  const hasHeader = data.section_title || data.section_subtitle || data.section_description;
  
  // Calculate width - default to 60%
  const embedWidth = data.embed_width || '60';

  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="embed-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasHeader && (
          <div className="text-center mb-12">
            {data.section_title && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {data.section_title}
              </h2>
            )}
            {data.section_subtitle && (
              <p className="text-xl text-gray-600 mb-4">
                {data.section_subtitle}
              </p>
            )}
            {data.section_description && (
              <div 
                className="text-lg text-gray-600 max-w-2xl mx-auto [&>p]:mb-2 [&>b]:font-semibold [&>strong]:font-semibold [&>i]:italic [&>em]:italic [&>a]:text-gray-600 [&>a]:underline [&>a:hover]:text-gray-400 [&>h1]:text-4xl [&>h1]:font-extrabold [&>h1]:mb-3 [&>h1]:text-gray-900 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:text-gray-900 [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:text-gray-800 [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mb-2 [&>h4]:text-gray-800 [&>h5]:text-lg [&>h5]:font-medium [&>h5]:mb-2 [&>h5]:text-gray-700 [&>h6]:text-base [&>h6]:font-medium [&>h6]:mb-1 [&>h6]:text-gray-700 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-2 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-2 [&>li]:mb-1"
                dangerouslySetInnerHTML={{ __html: data.section_description }}
              />
            )}
          </div>
        )}
        {/* Mobile: full width, Desktop: configured width centered using CSS custom property */}
        <style>{`
          @media (min-width: 768px) {
            .embed-width-container {
              max-width: ${embedWidth}% !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
          }
        `}</style>
        <div 
          ref={containerRef}
          className="embed-container embed-width-container w-full"
        />
        {data.cta_label && data.cta_link && (
          <div className="text-center mt-8">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Gallery Section Component with Lightbox
const GallerySection = ({ data, navigate }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const images = data?.images || [];

  const openLightbox = (index) => {
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  const goToPrev = useCallback((e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback((e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goToPrev, goToNext]);

  if (!images.length) return null;

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(data?.section_title || data?.section_description) && (
          <div className="text-center mb-12">
            {data?.section_title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {data.section_title}
              </h2>
            )}
            {data?.section_description && (
              <div 
                className="text-lg text-gray-600 max-w-2xl mx-auto [&>p]:mb-2 [&>b]:font-semibold [&>strong]:font-semibold [&>i]:italic [&>em]:italic [&>a]:text-gray-600 [&>a]:underline [&>a:hover]:text-gray-400 [&>h1]:text-4xl [&>h1]:font-extrabold [&>h1]:mb-3 [&>h1]:text-gray-900 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:text-gray-900 [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:text-gray-800 [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mb-2 [&>h4]:text-gray-800 [&>h5]:text-lg [&>h5]:font-medium [&>h5]:mb-2 [&>h5]:text-gray-700 [&>h6]:text-base [&>h6]:font-medium [&>h6]:mb-1 [&>h6]:text-gray-700 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-2 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-2 [&>li]:mb-1"
                dangerouslySetInnerHTML={{ __html: data.section_description }}
              />
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-xl aspect-square"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image}
                alt={`Gallery image ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-medium">
                  View
                </span>
              </div>
            </div>
          ))}
        </div>

        {data?.cta_label && data?.cta_link && (
          <div className="text-center mt-10">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>

      {/* Lightbox Modal with Prev/Next */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>

          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/50 hover:bg-black/70 rounded-full p-3 z-10"
            onClick={goToPrev}
          >
            <ChevronLeft size={32} />
          </button>

          <img
            src={images[selectedIndex]}
            alt={`Gallery image ${selectedIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/50 hover:bg-black/70 rounded-full p-3 z-10"
            onClick={goToNext}
          >
            <ChevronRight size={32} />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full text-sm">
            {selectedIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
};

// Sponsors Section Component
const SponsorsSection = ({ data, navigate }) => {
  const sponsors = data?.sponsors || [];

  if (!sponsors.length) return null;

  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid="sponsors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(data?.section_title || data?.section_description) && (
          <div className="text-center mb-12">
            {data?.section_title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {data.section_title}
              </h2>
            )}
            {data?.section_description && (
              <div 
                className="text-lg text-gray-600 max-w-2xl mx-auto [&>p]:mb-2 [&>b]:font-semibold [&>strong]:font-semibold [&>i]:italic [&>em]:italic [&>a]:text-gray-600 [&>a]:underline [&>a:hover]:text-gray-400 [&>h1]:text-4xl [&>h1]:font-extrabold [&>h1]:mb-3 [&>h1]:text-gray-900 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:text-gray-900 [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:text-gray-800 [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mb-2 [&>h4]:text-gray-800 [&>h5]:text-lg [&>h5]:font-medium [&>h5]:mb-2 [&>h5]:text-gray-700 [&>h6]:text-base [&>h6]:font-medium [&>h6]:mb-1 [&>h6]:text-gray-700 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-2 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-2 [&>li]:mb-1"
                dangerouslySetInnerHTML={{ __html: data.section_description }}
              />
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
          {sponsors.map((sponsor, index) => {
            const LogoElement = (
              <img
                src={sponsor.logo}
                alt={sponsor.name || `Sponsor ${index + 1}`}
                className="h-12 md:h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
              />
            );

            return sponsor.url ? (
              <a
                key={index}
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                title={sponsor.name || undefined}
              >
                {LogoElement}
              </a>
            ) : (
              <div key={index} title={sponsor.name || undefined}>
                {LogoElement}
              </div>
            );
          })}
        </div>

        {data?.cta_label && data?.cta_link && (
          <div className="text-center mt-10">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Press Section Component - 3-per-row layout for press quotes
const PressSection = ({ data, navigate }) => {
  const pressItems = data?.press_items || [];

  if (!pressItems.length) return null;

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="press-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        {(data?.section_title || data?.section_subtitle || data?.section_description) && (
          <div className="text-center mb-12">
            {data?.section_subtitle && (
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                {data.section_subtitle}
              </p>
            )}
            {data?.section_title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {data.section_title}
              </h2>
            )}
            {data?.section_description && (
              <div 
                className="text-lg text-gray-600 max-w-2xl mx-auto [&>p]:mb-2 [&>b]:font-semibold [&>strong]:font-semibold [&>i]:italic [&>em]:italic [&>a]:text-gray-600 [&>a]:underline [&>a:hover]:text-gray-400"
                dangerouslySetInnerHTML={{ __html: data.section_description }}
              />
            )}
          </div>
        )}

        {/* Press Quotes Grid - 3 per row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pressItems.map((item, index) => (
            <div 
              key={index} 
              className="bg-gray-50 rounded-xl p-6 flex flex-col border border-gray-100 hover:shadow-lg transition-shadow duration-300"
            >
              {/* Quote */}
              <blockquote className="flex-1">
                <p className="text-gray-700 italic leading-relaxed">
                  "{item.quote}"
                </p>
              </blockquote>
              
              {/* Attribution */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{item.publication}</p>
                    {item.date && (
                      <p className="text-sm text-gray-500">{item.date}</p>
                    )}
                  </div>
                </div>
                {item.article_name && item.article_link && (
                  <a
                    href={item.article_link}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="mt-3 inline-flex items-start text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <span>{item.article_name}</span>
                    <svg className="w-4 h-4 ml-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
                {item.article_name && !item.article_link && (
                  <p className="mt-3 text-sm text-gray-500">{item.article_name}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {data?.cta_label && data?.cta_link && (
          <div className="text-center mt-10">
            <a
              href={data.cta_link}
              onClick={(e) => handleLinkClick(e, data.cta_link, navigate)}
              className="inline-block px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              {data.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

// Newsletter Section Component - uses Flodesk embed with custom wrapper
const NewsletterSectionRenderer = ({ data }) => {
  const formId = data?.flodesk_form_id || '698ff949955577ffd7fda382';
  const title = data?.section_title || '';
  const subtitle = data?.section_subtitle || '';
  const bottomText = data?.bottom_text || 'Join 40,000+ subscribers already in our community';

  return (
    <section className="py-24 bg-gray-50" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          {/* Header with Icon */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
              <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            {title && (
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-lg text-gray-600">
                {subtitle}
              </p>
            )}
          </div>

          {/* Flodesk Form - Native styling preserved */}
          <div className="flodesk-form-container">
            <FlodeskForm formId={formId} />
          </div>

          {/* Bottom Text with Checkmark */}
          {bottomText && (
            <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
              <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {bottomText}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Section Renderer - wraps section with anchor ID
const SectionRenderer = ({ section, pageType, accentColor, navigate }) => {
  const anchor = section.anchor;
  
  const renderSection = () => {
    switch (section.type) {
      case 'hero':
        return <HeroSection data={section.data} pageType={pageType} accentColor={accentColor} navigate={navigate} />;
      case 'feature_grid':
        return <FeatureGridSection data={section.data} pageType={pageType} navigate={navigate} />;
      case 'cta_strip':
        return <CTAStripSection data={section.data} pageType={pageType} navigate={navigate} />;
      case 'image_text':
        return <ImageTextSection data={section.data} pageType={pageType} navigate={navigate} />;
      case 'video_text':
        return <VideoTextSection data={section.data} pageType={pageType} navigate={navigate} />;
      case 'text':
        return <TextSection data={section.data} navigate={navigate} />;
      case 'embed':
        return <EmbedSection data={section.data} navigate={navigate} />;
      case 'gallery':
        return <GallerySection data={section.data} navigate={navigate} />;
      case 'sponsors':
        return <SponsorsSection data={section.data} navigate={navigate} />;
      case 'press':
        return <PressSection data={section.data} navigate={navigate} />;
      case 'newsletter_flodesk':
        return <NewsletterSectionRenderer data={section.data} />;
      default:
        return null;
    }
  };

  const content = renderSection();
  if (!content) return null;

  // Wrap with anchor div if anchor is set
  if (anchor) {
    return <div id={anchor}>{content}</div>;
  }
  return content;
};

// Main Dynamic Page Component
const DynamicPage = () => {
  const { slug: rawSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Clean the slug (remove trailing slashes for API calls)
  const slug = rawSlug?.replace(/\/+$/, '') || rawSlug;

  // Redirect to trailing slash version if needed
  useEffect(() => {
    if (slug && !location.pathname.endsWith('/')) {
      navigate(`/${slug}/`, { replace: true });
    }
  }, [slug, location.pathname, navigate]);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) return;
      
      try {
        // Fetch page data
        const pageResponse = await axios.get(`${API}/pages/${slug}`);
        
        // Check if the response is a redirect instruction
        if (pageResponse.data.redirect) {
          const redirectTo = pageResponse.data.redirect_to;
          if (redirectTo) {
            // Handle external URLs
            if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
              window.location.href = redirectTo;
              return;
            }
            // Handle internal paths
            const cleanPath = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
            navigate(cleanPath, { replace: true });
            return;
          }
        }
        
        // Fetch settings
        const settingsResponse = await axios.get(`${API}/settings`);
        
        setPage(pageResponse.data);
        setSettings(settingsResponse.data);
        
        // Update document title
        if (pageResponse.data.meta_title) {
          document.title = pageResponse.data.meta_title;
        }
        
        // Update or create meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        if (pageResponse.data.meta_description) {
          metaDescription.setAttribute('content', pageResponse.data.meta_description);
        } else if (settingsResponse.data.default_meta_description) {
          metaDescription.setAttribute('content', settingsResponse.data.default_meta_description);
        }
        
        // Update or create og:image meta tag
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        // Always set the og:image (backend already handles fallback to default)
        ogImage.setAttribute('content', pageResponse.data.og_image || '');
        
        // Also update twitter:image
        let twitterImage = document.querySelector('meta[property="twitter:image"]');
        if (!twitterImage) {
          twitterImage = document.createElement('meta');
          twitterImage.setAttribute('property', 'twitter:image');
          document.head.appendChild(twitterImage);
        }
        twitterImage.setAttribute('content', pageResponse.data.og_image || '');
        
        // Update or create og:title
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
          ogTitle = document.createElement('meta');
          ogTitle.setAttribute('property', 'og:title');
          document.head.appendChild(ogTitle);
        }
        ogTitle.setAttribute('content', pageResponse.data.meta_title || pageResponse.data.title || 'Pulse NYC');
        
        // Update or create og:description
        let ogDescription = document.querySelector('meta[property="og:description"]');
        if (!ogDescription) {
          ogDescription = document.createElement('meta');
          ogDescription.setAttribute('property', 'og:description');
          document.head.appendChild(ogDescription);
        }
        if (pageResponse.data.meta_description) {
          ogDescription.setAttribute('content', pageResponse.data.meta_description);
        } else if (settingsResponse.data.default_meta_description) {
          ogDescription.setAttribute('content', settingsResponse.data.default_meta_description);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug, navigate]);

  // Handle hash anchor scrolling after page loads, or scroll to top if no hash
  useEffect(() => {
    if (!loading && page) {
      if (location.hash) {
        // Small delay to ensure DOM is fully rendered
        const timeoutId = setTimeout(() => {
          const hash = location.hash.slice(1); // Remove the # character
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        
        return () => clearTimeout(timeoutId);
      } else {
        // No hash - scroll to top of the page
        window.scrollTo(0, 0);
      }
    }
  }, [loading, page, location.hash, slug]);

  // Check if the page has a dark hero (hero section with background image or dark page type)
  const hasDarkHero = () => {
    if (!page?.sections?.length) return false;
    const firstSection = page.sections[0];
    if (firstSection?.type !== 'hero') return false;
    
    // Has background image OR is expo/festival type (which have dark backgrounds)
    return !!(firstSection.data?.background_image || 
              page.page_type === 'expo' || 
              page.page_type === 'festival');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar pageSlug={slug} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Go Home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid={`page-${slug}`}>
      <Navbar pageSlug={slug} darkHero={hasDarkHero()} />
      <main className="flex-1">
        {page?.sections?.map((section) => (
          <SectionRenderer
            key={section.id}
            section={section}
            pageType={page.page_type}
            accentColor={settings?.accent_color}
            navigate={navigate}
          />
        ))}
      </main>
      <Footer />
    </div>
  );
};

export default DynamicPage;
